import { action } from "mobx";
import { ProfileKeyCode } from "../DynamicProfileEnums";
import { DynamicTableStoreBase } from "../DynamicTableStoreBase";
import { DynamicCrossSectionHub } from "./DynamicCrossSectionHub";
import { XyzPoint } from "cd-eft/models/SurveyEngineering/XyzPoint";
import { getDistance2D } from 'cd-eft/components/SurveyEngineering/Maps/SETMapFunctions/elevationInterpolationEngine';

class DynamicCrossSectionTableStore extends DynamicTableStoreBase {
    protected hub: DynamicCrossSectionHub;

    private _initialCrossSectionPoint;

    constructor(hub: DynamicCrossSectionHub) {
        super();
        this.hub = hub;
        this.profileKeys = [ProfileKeyCode.CSD, ProfileKeyCode.CSO,ProfileKeyCode.WTL];
    }

    // adapt to global stores
    set setStore(setStore) {
        this.hub.setStore = setStore;
    }

    get setStore() {
        return this.hub.setStore;
    }

    public set crossSectionStore(store) {
        this.hub.crossSectionStore = store;
    }

    public get crossSectionStore() {
        return this.hub.crossSectionStore;
    }

    // Get the initial cross section point
    private get initialCrossSectionPoint() {
        if (!this._initialCrossSectionPoint) {
            this._initialCrossSectionPoint = this.setStore.initialCrossSectionPoint;
        }
        return this._initialCrossSectionPoint;
    }

    /** private methods */
    @action
    protected async addRow(activePoint: XyzPoint, eventState: string, vertexIndex: number, originalPoint: XyzPoint | undefined = undefined, blockTable: boolean = false) {
        // blockTable prevents the table from being updated when the original profile gets drawn under the design profile
        if (!blockTable) {
            let originalData;
            let designData;
            if (originalPoint) {
                originalData = originalPoint;
                designData = activePoint;
            } else {
                originalData = activePoint;
            }
            // Add a new row to the cross section points
            const points = this.currentObject.crossSectionPoints;
            const newRow = { ...this.initialCrossSectionPoint };
            newRow.x = activePoint.getX().toFixed(this.precision);
            newRow.y = activePoint.getY().toFixed(this.precision);
            if (Number.isFinite(originalData.getZ())) {
                newRow.originalElevation = originalData.getZ()?.toFixed(this.precision);
            } else {
                newRow.originalElevation = originalData.getZ()?.designZ?.toFixed(this.precision);
            }
            if (designData) newRow.designElevation = designData.getZ()?.toFixed(this.precision);

            // Set the beginningX and beginningY properties if it's the start event
            if (eventState === 'start') {
                this.currentObject.beginningX = newRow.x;
                this.currentObject.beginningY = newRow.y;
            }

            // Update the cross section point at the specified vertex index
            if (vertexIndex <= points.length - 1) {
                points[vertexIndex] = newRow;
            } else {
                points.push({ ...newRow });
                vertexIndex = points.length - 1;
            }

            // Calculate point distance and point name
            if (vertexIndex > 0) {
                const p1 = new XyzPoint(points[vertexIndex - 1].x, points[vertexIndex - 1].y, 0);
                const p2 = new XyzPoint(points[vertexIndex].x, points[vertexIndex].y, 0);
                const dist = +points[vertexIndex - 1].pointDistance + getDistance2D(p1, p2);
                points[vertexIndex].pointDistance = dist.toFixed(this.precision);
                points[vertexIndex].sectionPoint = Math.round(dist);
            }

            // Calculate cut/fill height
            if (designData) {
                let difference = newRow.originalElevation - newRow.designElevation;
                if (difference.toFixed(1) === '-0.0') difference = 0;
                points[vertexIndex].cutFillHeight = difference;
            }
        }
    }

    @action
    protected async insertRow(activePoint: XyzPoint, vertexIndex: number, originalPoint: XyzPoint | undefined = undefined, blockTable: boolean = false) {
        if (!blockTable) {
            let originalData;
            let designData;
            if (originalPoint) {
                originalData = originalPoint;
                designData = activePoint;
            } else {
                originalData = activePoint;
            }

            if (vertexIndex < 0) return;
            const newRow = { ...this.initialCrossSectionPoint };
            newRow.x = activePoint.getX().toFixed(this.precision);
            newRow.y = activePoint.getY().toFixed(this.precision);
            newRow.originalElevation = originalData.getZ()?.toFixed(this.precision);
            if (designData) newRow.designElevation = designData.getZ()?.toFixed(this.precision);

            const points = this.currentObject.crossSectionPoints;
            points.splice(vertexIndex, 0, newRow);


            if (vertexIndex === 0) {
                this.calculateStationDistances();
            } else {
                const p1 = new XyzPoint(points[vertexIndex - 1].x, points[vertexIndex - 1].y, 0);
                const p2 = new XyzPoint(points[vertexIndex].x, points[vertexIndex].y, 0);
                const dist = +points[vertexIndex - 1].pointDistance + getDistance2D(p1, p2);
                points[vertexIndex].pointDistance = dist.toFixed(this.precision);
                points[vertexIndex].pointName = Math.round(dist);
                points[vertexIndex].pointName = Math.round(dist);
            }

            if (designData) points[vertexIndex].cutFillHeight = newRow.originalElevation - newRow.designElevation;
        }
    }

    protected reset() {
        // Reset the cross section points
        if (this.currentObject) {
            this.currentObject.crossSectionPoints.length = 0;
            const newRow = { ...this.initialCrossSectionPoint };
            this.currentObject.crossSectionPoints.push(newRow);

            this.currentObject.crossSectionEntryMethod = 'Coordinates';
        }
    }

    protected calculateStationDistances() {
        // Calculate point distances for each cross section point
        const points = this.currentObject.crossSectionPoints;
        let p1 = new XyzPoint(points[0].x, points[0].y, 0);
        points[0].pointDistance = 0;
        for (let index = 1; index < points.length; index++) {
            const p2 = new XyzPoint(points[index].x, points[index].y, 0);
            const dist = +points[index - 1].pointDistance + getDistance2D(p1, p2);
            points[index].pointDistance = dist.toFixed(this.precision);
            points[index].pointName = Math.round(dist);

            p1 = p2;
        }
    }

    protected addRows() { }
    protected insertRows(info: any, vertexIndex: number) { }

    protected modifyRows(info: any, vertexIndex: number) { }

    async prepareTablePoints(profileData) {
        if (profileData) {
            if (profileData.data.designProfile) {
                const designStations = profileData.data.designProfile.stationDTOList;
                const tablePoints = designStations.filter((designPoint) => 
                    designPoint.inputPoint.description === 'structurePoint' || designPoint.inputPoint.description === 'CrossVertex'
                );
                await this.populateSingleCrossSectionTable(tablePoints, true);
            } else {
                const originalStations = profileData.data.originalProfile.stationDTOList;
                const tablePoints = originalStations.filter((originalPoint) => 
                    originalPoint.inputPoint.description === 'CrossVertex'
                );
                await this.populateSingleCrossSectionTable(tablePoints, false);
            }
        }
    }
    async populateSingleCrossSectionTable(tablePoints, design: boolean) {
        this.currentObject.beginningX = tablePoints[0].inputPoint.x;
        this.currentObject.beginningY = tablePoints[0].inputPoint.y;
        this.currentObject.crossSectionPoints = [];
        const crossSectionPoints = this.currentObject.crossSectionPoints;
        for (const point of tablePoints) {
            const newRow = { ...this.initialCrossSectionPoint };
            newRow.x = point.inputPoint.x.toFixed(this.precision);
            newRow.y = point.inputPoint.y.toFixed(this.precision);
            newRow.designElevation = design ? point.inputPoint.z.toFixed(this.precision) : 0.0;
            newRow.originalElevation = design ? (point.inputPoint.z.toFixed(this.precision) - point.inputPoint.cutOrFill) : point.inputPoint.z.toFixed(this.precision);
            newRow.pointDistance = point.distance.toFixed(this.precision);
            newRow.sectionPoint = Math.round(point.distance);
            newRow.cutFillHeight = point.inputPoint.cutOrFill;
            if (newRow.cutFillHeight.toFixed(1) === '-0.0') newRow.cutFillHeight = 0;
            crossSectionPoints.push({ ...newRow });
        }
    }
}

export { DynamicCrossSectionTableStore };